import { errorHandler } from "@/lib/utils";
import {
  announcementsApi,
  type Announcement,
} from "@/services/api/announcements";
import { useCallback, useState } from "react";

export function useAnnouncements(userId: number | null) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [recentAnnouncements, setRecentAnnouncements] = useState<
    Announcement[]
  >([]);
  const [loadingUnread, setLoadingUnread] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(false);

  const refresh = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!userId) {
        setAnnouncements([]);
        return;
      }
      if (!options?.silent) {
        setLoadingUnread(true);
      }
      try {
        const list = await announcementsApi.listUnread();
        setAnnouncements(list);
      } catch (error) {
        errorHandler(error, "Erro ao buscar anúncios");
      } finally {
        if (!options?.silent) {
          setLoadingUnread(false);
        }
      }
    },
    [userId],
  );

  const refreshRecent = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!userId) {
        setRecentAnnouncements([]);
        return;
      }
      if (!options?.silent) {
        setLoadingRecent(true);
      }
      try {
        const list = await announcementsApi.listRecent();
        setRecentAnnouncements(list);
      } catch (error) {
        errorHandler(error, "Erro ao buscar anúncios recentes");
      } finally {
        if (!options?.silent) {
          setLoadingRecent(false);
        }
      }
    },
    [userId],
  );

  const markAsRead = useCallback((id: number) => {
    setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    setRecentAnnouncements((prev) =>
      prev.map((a) => (a.id === id ? { ...a, is_read: true } : a)),
    );
    void announcementsApi.markAsRead(id).catch((error) => {
      errorHandler(error, "Erro ao marcar anúncio como lido");
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    setAnnouncements([]);
    setRecentAnnouncements((prev) =>
      prev.map((a) => ({ ...a, is_read: true })),
    );
    void announcementsApi.markAllAsRead().catch((error) => {
      errorHandler(error, "Erro ao marcar todos os anúncios como lidos");
    });
  }, []);

  return {
    announcements,
    recentAnnouncements,
    loading: loadingUnread || loadingRecent,
    refresh,
    refreshRecent,
    markAsRead,
    markAllAsRead,
    unreadCount: announcements.length,
  };
}
